import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useId,
  useState,
} from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';

import { Code } from '@components/patterns';
import { getPlainText } from '@components/patterns/Code/process';
import { CodeVariableMap } from '@components/patterns/Code/types';
import { Language, LANGUAGE_LABELS } from '@components/patterns/Code/utilities';
import { CopyButton } from '@components/patterns/CopyButton';
import { cn, easeOutSmooth } from '@components/primitives';
import { toast } from '@vault';

import css from './styles.module.css';

export interface File {
  icon?: ReactNode;
  label?: string;
  language: Language;
  name?: string;
  content: string;
}

function fileKey(file: File) {
  return file.name ?? file.label ?? file.language;
}

export interface CodeBlockTabProps extends PropsWithChildren {
  file: File;
  variables?: CodeVariableMap;
  onCopy?(file: File): void;
}

function CodeBlockTab({
  file,
  variables,
  onCopy,
  children,
}: CodeBlockTabProps) {
  const getCode = useCallback(
    async () => getPlainText(file.content, variables),
    [file.content, variables]
  );
  const handleCopy = useCallback(() => {
    toast('Copied code');
    onCopy?.(file);
  }, [file, onCopy]);

  return (
    <TabsContent className={css.tabPanel} value={fileKey(file)}>
      {file.name ? (
        <div className={css.tabHeader}>
          <p className={css.fileName}>{file.name}</p>
          <CopyButton
            size="2xs"
            flush
            title="Copy code"
            value={getCode}
            onCopy={handleCopy}
          />
        </div>
      ) : (
        <div className={css.floatingCopy}>
          <CopyButton
            size="xs"
            flush
            title="Copy code"
            value={getCode}
            onCopy={handleCopy}
            ghost={false}
          />
        </div>
      )}

      <div className={css.tabContent}>{children}</div>
    </TabsContent>
  );
}

export interface CodeBlockProps {
  className?: string;
  files: File[];
  /** The tab open at first, when `openFile` isn't set. */
  initialFile?: string;
  /** The maximum number of lines to display */
  lines?: number;
  /** The number of lines to display in preview mode. Default is 0 (disabled) */
  previewLines?: number;
  /** Whether to show line numbers. Default is true */
  lineNumbers?: boolean;
  /** Variables to replace in the code. */
  variables?: CodeVariableMap;
  /** The open tab's file `name`, `label` or `language`, to choose it from outside. */
  openFile?: string;
  /** Called with the file key when another tab is opened. */
  onOpenFileChange?(openFile: string): void;
  /** Extra controls at the end of the tab bar, such as a docs link. */
  actions?: ReactNode;
  /** Called after a file's code is copied. */
  onCopy?(file: File): void;
}

export function CodeBlock({
  className,
  files,
  initialFile,
  lines = 15,
  previewLines = 0,
  lineNumbers = true,
  variables,
  openFile,
  onOpenFileChange,
  actions,
  onCopy,
}: CodeBlockProps) {
  const [previewActive, setPreviewActive] = useState(true);

  const [internalValue, setInternalValue] = useState(
    initialFile ?? (files.length > 0 ? fileKey(files[0]) : undefined)
  );
  const value = openFile ?? internalValue;
  const setValue = (file: string) => {
    setInternalValue(file);
    onOpenFileChange?.(file);
  };

  // One per block, so the tab underline doesn't slide between blocks on a page
  const layoutGroupId = useId();

  return (
    <Tabs
      className={cn(css.tabs, className)}
      value={value}
      onValueChange={setValue}
    >
      <AnimatePresence>
        <LayoutGroup id={layoutGroupId}>
          <div className={cn(actions && css.tabBar)}>
            <TabsList
              loop
              className={cn(css.tabList, files.length < 2 && 'sr-only')}
            >
              {files.map((file) => (
                <TabsTrigger
                  key={fileKey(file)}
                  className={css.tabItem}
                  value={fileKey(file)}
                >
                  {file.icon && (
                    <span className={css.tabItemIcon} aria-hidden>
                      {file.icon}
                    </span>
                  )}

                  <span className={css.tabItemLabel}>
                    {file.label ??
                      LANGUAGE_LABELS[file.language] ??
                      file.language}
                  </span>

                  {fileKey(file) === value && (
                    <motion.div
                      layoutId="indicator"
                      className={css.tabIndicator}
                      transition={{
                        duration: 0.2,
                        ease: easeOutSmooth,
                      }}
                    />
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
            {actions && <div className={css.tabActions}>{actions}</div>}
          </div>
        </LayoutGroup>
      </AnimatePresence>

      {files.map((file) => (
        <CodeBlockTab
          key={fileKey(file)}
          file={file}
          variables={variables}
          onCopy={onCopy}
        >
          <Code
            className={css.tabContentCode}
            language={file.language}
            lineNumbers={lineNumbers}
            lines={lines}
            previewLines={previewLines}
            previewActive={previewActive}
            onPreviewActiveChange={setPreviewActive}
            variables={variables}
          >
            {file.content}
          </Code>
        </CodeBlockTab>
      ))}
    </Tabs>
  );
}
